import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "454",
  "slug": "what-is-an-atoll",
  "title": "What Is an Atoll? How Coral Rings Form in the Open Ocean",
  "excerpt": "An atoll is a ring-shaped coral reef encircling a lagoon, often the last trace of a sunken volcano. Here is the elegant theory behind them and where to find the most striking examples.",
  "date": "2026-06-16",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1442386967425-6205da77c3ed?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxjb3JhbCUyMGF0b2xsJTIwbGFnb29uJTIwYWVyaWFsfGVufDF8MHx8fDE3ODE1OTc2NjN8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "An atoll is a ring-shaped coral reef or island that encloses a central lagoon. Most form as a volcanic island slowly sinks while its fringing coral keeps growing upward, a process Charles Darwin first explained in 1842.",
  "content": [
    {
      "type": "paragraph",
      "text": "Few shapes in nature are as instantly recognizable from above as an atoll: a thin ring of reef and sand wrapped around a brilliant turquoise lagoon, floating alone in the deep blue of the open ocean. An atoll is a coral structure, roughly circular or horseshoe-shaped, that encloses a body of shallow water called a lagoon. Many of them are the only thing left of an island that has long since vanished beneath the waves."
    },
    {
      "type": "paragraph",
      "text": "Atolls are some of the most remote inhabited places on the planet and, increasingly, some of the most threatened. The story of how they form is one of the most elegant ideas in all of geography."
    },
    {
      "type": "heading",
      "text": "Darwin’s Beautiful Theory"
    },
    {
      "type": "paragraph",
      "text": "In 1842, Charles Darwin proposed an explanation for atolls that still stands today. It begins with a volcanic island rising from the sea. Coral reefs grow in the warm, sunlit shallows around its shore, forming a fringing reef. Over millions of years, the volcano cools and slowly subsides, sinking back into the ocean."
    },
    {
      "type": "paragraph",
      "text": "As the island sinks, the coral keeps growing upward toward the light, building on the skeletons of older coral. The reef becomes a barrier reef, separated from the shrinking island by a widening lagoon. Eventually the original island disappears completely beneath the surface, leaving only the ring of living coral that marks where it once stood. That ring is an atoll."
    },
    {
      "type": "heading",
      "text": "Fringing Reef, Barrier Reef, Atoll"
    },
    {
      "type": "paragraph",
      "text": "Darwin’s insight ties three reef types into a single life story, which makes them easy to keep straight."
    },
    {
      "type": "list",
      "items": [
        "Fringing reef — coral grows directly against the shore of a young volcanic island.",
        "Barrier reef — the island has subsided, leaving a lagoon between the shore and the reef.",
        "Atoll — the island is gone entirely, leaving only the encircling reef around an open lagoon."
      ]
    },
    {
      "type": "heading",
      "text": "Where to Find Them"
    },
    {
      "type": "paragraph",
      "text": "Atolls cluster in the warm tropical waters of the Indian and Pacific oceans, where reef-building coral thrives."
    },
    {
      "type": "list",
      "items": [
        "The Maldives, a nation of around 1,200 islands spread across 26 natural atolls in the Indian Ocean.",
        "The Marshall Islands, including Kwajalein, which encloses one of the largest lagoons in the world.",
        "Aldabra in the Seychelles, a near-pristine atoll famous for its giant tortoises and protected as a UNESCO World Heritage Site.",
        "The Tuamotu Archipelago of French Polynesia, one of the largest chains of atolls anywhere."
      ]
    },
    {
      "type": "heading",
      "text": "Life on the Edge"
    },
    {
      "type": "paragraph",
      "text": "Atolls are spectacular but precarious. Most rise only a metre or two above sea level, which makes them acutely vulnerable to rising seas, storm surge, and saltwater creeping into their freshwater supplies. For nations like the Maldives and the Marshall Islands, sea-level rise is not a distant abstraction; it is an existential question about whether their homeland will remain above water."
    },
    {
      "type": "paragraph",
      "text": "That fragility is matched by extraordinary biodiversity. The lagoon and reef of a healthy atoll teem with fish, and the coral itself is a living structure built by billions of tiny animals over thousands of years."
    },
    {
      "type": "heading",
      "text": "How Darwin Was Proven Right"
    },
    {
      "type": "paragraph",
      "text": "For more than a century, Darwin’s subsidence theory was an elegant idea without direct proof. The confirmation came in a surprising place. In the early 1950s, scientists drilled deep into Enewetak Atoll in the Marshall Islands and struck volcanic rock more than a kilometre below the surface, buried under an immense thickness of ancient coral. That was exactly what Darwin had predicted: a sunken volcano capped by reef that had grown upward for millions of years as the island sank."
    },
    {
      "type": "paragraph",
      "text": "The discovery turned atolls into natural records of the past. The layered coral of an atoll preserves a long history of sea level and ocean temperature, which is one reason researchers study them so closely as the climate changes today. Few landforms pack so much of Earth’s story into so thin a ring of sand, and fewer still owe their existence to a process this counterintuitive: an island that builds upward by sinking."
    },
    {
      "type": "heading",
      "text": "Spotting an Atoll From Above"
    },
    {
      "type": "paragraph",
      "text": "No other landform looks quite like an atoll from orbit. Search for a slender, often broken ring of pale reef and sand enclosing a calm, light-colored lagoon, set against the dark deep water all around. The contrast between the shallow turquoise inside and the abyssal blue outside is unmistakable."
    }
  ]
};
